import { CURRENT_SAVE_VERSION, defaultPreferences, type GameSave } from "./GameSave";

export class SaveMigrationError extends Error {}

/**
 * Legacy pre-1.0 prototype save shape, kept only so old saves keep loading: player
 * volume used to be a single number instead of the current `preferences` object.
 */
interface LegacyV0Save {
  readonly version: 0;
  readonly world: GameSave["world"];
  readonly player: {
    readonly inventory: GameSave["player"]["inventory"];
    readonly knowledge: GameSave["player"]["knowledge"];
    readonly volume?: number;
  };
}

/** V1 saves had preferences inside player snapshot. */
interface V1Save {
  readonly version: 1;
  readonly world: GameSave["world"];
  readonly player: {
    readonly inventory: GameSave["player"]["inventory"];
    readonly knowledge: GameSave["player"]["knowledge"];
    readonly preferences?: unknown;
  };
}

/** V2 saves stored full entity snapshots per scene. */
interface V2Save {
  readonly version: 2;
  readonly world: {
    readonly currentSceneId: string;
    readonly scenes: Readonly<Record<string, unknown>>;
    readonly revealedSecrets: readonly string[];
    readonly questProgress: Readonly<Record<string, unknown>>;
  };
  readonly player: GameSave["player"];
}

function migrateV0ToV1(save: LegacyV0Save): V1Save {
  return {
    version: 1,
    world: save.world,
    player: {
      inventory: save.player.inventory,
      knowledge: save.player.knowledge,
      preferences: { ...defaultPreferences(), musicVolume: save.player.volume ?? defaultPreferences().musicVolume },
    },
  };
}

function migrateV1ToV2(save: V1Save): V2Save {
  return {
    version: 2,
    world: save.world as V2Save["world"],
    player: {
      inventory: save.player.inventory,
      knowledge: save.player.knowledge,
    },
  };
}

function migrateV2ToV3(save: V2Save): GameSave {
  return {
    version: 3,
    world: {
      currentSceneId: save.world.currentSceneId,
      sceneDiffs: {},
      revealedSecrets: save.world.revealedSecrets,
      questProgress: save.world.questProgress as GameSave["world"]["questProgress"],
    },
    player: save.player,
  };
}

const migrations: Readonly<Record<number, (save: never) => GameSave | never>> = {
  0: migrateV0ToV1 as (save: never) => GameSave,
  1: migrateV1ToV2 as unknown as (save: never) => GameSave,
  2: migrateV2ToV3 as (save: never) => GameSave,
};

/** Upgrades a raw, possibly-legacy save to {@link CURRENT_SAVE_VERSION}. */
export function migrateSave(raw: unknown): GameSave {
  if (typeof raw !== "object" || raw === null || typeof (raw as { version?: unknown }).version !== "number") {
    throw new SaveMigrationError("Save data missing a numeric version");
  }
  let save = raw as { version: number };
  while (save.version < CURRENT_SAVE_VERSION) {
    const migrate = migrations[save.version];
    if (!migrate) throw new SaveMigrationError(`No migration registered from version ${save.version}`);
    save = migrate(save as never);
  }
  if (save.version > CURRENT_SAVE_VERSION) {
    throw new SaveMigrationError(`Save version ${save.version} is newer than supported version ${CURRENT_SAVE_VERSION}`);
  }
  return save as GameSave;
}
